import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import { queryKeys } from '@/constant/common'
import { useStationDeletionRefresh } from '@/features/config/hooks/station/useStationDeletionRefresh'
import { normalizeApiError } from '@/helpers/apiError'
import { NotifyService } from '@/helpers/notify'
import { useTranslation } from '@/hooks/useTranslation'
import { isStationDeletionBlocked, loadStationDeletionImpact, StationDeletionImpactError } from './stationDeletion'
import { stationDeletionSnapshotSchema } from './stationDeletionSnapshot'
import type { StationDeletionSnapshot, StationDeletionSource } from './stationDeletionSnapshot'

const STATUS_REFRESH_MS = 5000
const RUNNING_STATES = new Set(['started', 'running', 'active', 'wait start', 'wait stop', 'soaking', 'rain soaking'])
const isReportedRunning = (station: StationDeletionSource): boolean =>
  station.isRunning === true || RUNNING_STATES.has(station.stateName.trim().toLowerCase())

interface StationDeletionOptions {
  interfaceId: number
  station: StationDeletionSource
  allowed: boolean
  disabled: boolean
  onDeleted: () => void
}

export function useStationDeletion({ interfaceId, station, allowed, disabled, onDeleted }: StationDeletionOptions) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const refresh = useStationDeletionRefresh()
  const [open, setOpen] = useState(false)
  const confirming = useRef(false)
  const validIdentity = Number.isSafeInteger(interfaceId) && interfaceId > 0 && Number.isSafeInteger(station.id) && station.id > 0
  const statusKey = [queryKeys.stations, 'delete-safety', interfaceId, station.id]

  const readCurrentStation = async (): Promise<StationDeletionSnapshot> => {
    const response = await stationDataConfigApi.getById(String(interfaceId), String(station.id))
    const parsed = stationDeletionSnapshotSchema.safeParse(response.data)
    if (!parsed.success || parsed.data.id !== station.id) throw new Error(t('stationDeleteStateUnavailable'))
    return parsed.data
  }

  const deletion = useMutation(async () => {
    if (!allowed || disabled || !validIdentity) throw new Error(t('stationDeleteUnavailable'))
    const latest = await readCurrentStation()
    queryClient.setQueryData(statusKey, latest)
    if (isStationDeletionBlocked(latest)) throw new Error(t(isReportedRunning(latest) ? 'stationDeleteRunning' : 'stationDeleteStateUnavailable'))
    await stationDataConfigApi.delete(String(interfaceId), String(station.id))
  }, {
    onSuccess: async () => {
      await refresh(interfaceId, station.id)
      NotifyService.success(t('stationDeleteSuccess'))
      setOpen(false)
      onDeleted()
    },
    onSettled: () => { confirming.current = false },
  })

  const status = useQuery(statusKey, readCurrentStation, {
    enabled: allowed && validIdentity && !deletion.isLoading && !deletion.isSuccess,
    refetchInterval: deletion.isLoading || deletion.isSuccess ? false : STATUS_REFRESH_MS,
    retry: false,
    staleTime: 0,
  })
  const currentStation = status.data ?? station
  const running = isReportedRunning(currentStation)
  const blocked = isStationDeletionBlocked(currentStation)
  const impact = useQuery(['station-delete-impact', interfaceId, station.id, status.data?.etZone], () => loadStationDeletionImpact(interfaceId, station.id, status.data?.etZone), {
    enabled: open && allowed && validIdentity && !deletion.isLoading && !deletion.isSuccess,
    retry: false,
    staleTime: 0,
    cacheTime: 0,
  })

  const unavailable = !allowed || disabled || !validIdentity || blocked || !status.isSuccess || status.isFetching || deletion.isLoading || deletion.isSuccess
  const confirmDisabled = unavailable || !impact.isSuccess || impact.isFetching
  const error = deletion.error ?? status.error ?? impact.error
  const errorMessage = error instanceof StationDeletionImpactError
    ? t('stationDeleteImpactUnavailable')
    : error ? normalizeApiError(error, t('stationDeleteFailed')).message
      : blocked && !running ? t('stationDeleteStateUnavailable') : undefined

  return {
    open, station: status.data, impact, running, unavailable, confirmDisabled,
    errorMessage, deleting: deletion.isLoading,
    openDialog: () => {
      if (unavailable) return
      deletion.reset()
      setOpen(true)
    },
    closeDialog: () => { if (!deletion.isLoading) setOpen(false) },
    confirm: () => {
      if (confirmDisabled || confirming.current) return
      confirming.current = true
      deletion.mutate()
    },
    retry: () => { void status.refetch(); void impact.refetch() },
  }
}
